import * as Coords from './coordinates'

export class CanvasItems {
  drawables: Set<Drawable>
  moveables: Set<Moveable>
  animations: Set<Drawable>
  detail: Projectile | null
  constructor(
    drawables: Set<Drawable>,
    moveables: Set<Moveable>,
    animations: Set<Drawable>,
    detail?: Projectile,
  ) {
    this.drawables = drawables
    this.moveables = moveables
    this.animations = animations
    this.detail = detail || null
  }
}

export interface Drawable {
  location: Coords.Coordinate
  aabb: [Coords.Coordinate, Coords.Coordinate]
  draw(ctx: any): void
}

export interface Moveable extends Drawable {
  vector: Coords.Vector2d
  contained: boolean
  mass: number
  locationHistory: Set<Coords.Coordinate>
  locationHistorySize: number
  drawDetail(ctx: any): void
  move(): void
  containsPoint(point: Coords.Coordinate): boolean
  reverseDy(): void
  reverseDx(): void
  setNewVelocity(magnitude: number): void
}

export class Crosshair implements Drawable {
  location: Coords.Coordinate
  radius: number
  color: string
  centerSpacing: number
  aabb: [Coords.Coordinate, Coords.Coordinate]
  constructor(
    location: Coords.Coordinate,
    hairLength: number,
    centerSpacing: number = 2,
    color: string = 'black',
  ) {
    this.location = location
    this.color = color
    this.centerSpacing = centerSpacing
    this.radius = centerSpacing + hairLength
    this.aabb = [
      new Coords.Coordinate(
        this.location.x - this.radius,
        this.location.y - this.radius,
      ),
      new Coords.Coordinate(
        this.location.x + this.radius,
        this.location.y + this.radius,
      ),
    ]
  }

  draw(ctx: any) {
    ctx.beginPath()
    // Left hair
    ctx.moveTo(this.location.x - this.radius, this.location.y)
    ctx.lineTo(this.location.x - this.centerSpacing, this.location.y)
    // Right hair
    ctx.moveTo(this.location.x + this.centerSpacing, this.location.y)
    ctx.lineTo(this.location.x + this.radius, this.location.y)
    // Top hair
    ctx.moveTo(this.location.x, this.location.y + this.radius)
    ctx.lineTo(this.location.x, this.location.y + this.centerSpacing)
    // Bottom hair
    ctx.moveTo(this.location.x, this.location.y - this.centerSpacing)
    ctx.lineTo(this.location.x, this.location.y - this.radius)
    ctx.closePath()

    ctx.strokeStyle = this.color
    ctx.stroke()
  }

  setNewLocation(location: Coords.Coordinate) {
    this.location = location
  }
}

export class Circle implements Drawable {
  location: Coords.Coordinate
  radius: number
  fill: string
  aabb: [Coords.Coordinate, Coords.Coordinate]
  constructor(location: Coords.Coordinate, radius: number, fill?: string) {
    this.location = location
    this.radius = radius
    this.fill = fill || 'black'
    this.aabb = [
      new Coords.Coordinate(
        this.location.x - this.radius,
        this.location.y - this.radius,
      ),
      new Coords.Coordinate(
        this.location.x + this.radius,
        this.location.y + this.radius,
      ),
    ]
  }

  draw(ctx: any) {
    ctx.beginPath()
    ctx.arc(
      this.location.x,
      this.location.y,
      this.radius,
      0,
      Math.PI * 2,
      false,
    )
    ctx.fillStyle = this.fill
    ctx.fill()
    ctx.closePath()
  }

  setNewLocation(location: Coords.Coordinate) {
    this.location = location
  }
}

export class Projectile extends Circle implements Moveable {
  location: Coords.Coordinate
  locationHistory: Set<Coords.Coordinate>
  locationHistorySize: number
  fill: string
  radius: number
  contained: boolean = false
  aabb: [Coords.Coordinate, Coords.Coordinate]
  vector: Coords.Vector2d
  mass: number
  constructor(
    location: Coords.Coordinate,
    target: Coords.Coordinate,
    magnitude: number,
    radius: number,
    contained: boolean = false,
    fill: string = 'red',
  ) {
    super(location, radius, fill)
    this.location = location
    this.locationHistory = new Set([this.location])
    this.locationHistorySize = 10
    this.fill = fill
    this.radius = radius
    this.contained = contained
    this.aabb = [
      new Coords.Coordinate(
        this.location.x - this.radius,
        this.location.y - this.radius,
      ),
      new Coords.Coordinate(
        this.location.x + this.radius,
        this.location.y + this.radius,
      ),
    ]
    const slopeToTarget = Coords.Slope.getSlopeFromCoordinates(location, target)
    const vectorSlope = Coords.Vector2d.getVectorSlope(slopeToTarget, magnitude)
    this.vector = new Coords.Vector2d(vectorSlope)
    // For now, mass is simply the area
    this.mass = Math.PI * Math.pow(this.radius, 2)
  }

  move() {
    this.location = this.vector.getNextPoint(this.location)
  }

  drawDetail(ctx: any) {
    this.drawLocationHistory(ctx)
    this.drawHighlight(ctx)
  }

  private drawLocationHistory(ctx: any) {
    ctx.beginPath()
    this.locationHistory.forEach((location) => {
      ctx.lineTo(location.x, location.y)
    })
    ctx.lineTo(this.location.x, this.location.y)
    ctx.strokeStyle = '#FF8200'
    ctx.stroke()
    ctx.closePath()
  }

  private drawHighlight(ctx: any) {
    ctx.beginPath()
    ctx.arc(
      this.location.x,
      this.location.y,
      this.radius + 2,
      0,
      Math.PI * 2,
      false,
    )
    ctx.strokeStyle = '#519BF6'
    ctx.lineWidth = 4
    ctx.stroke()
    ctx.closePath()
    ctx.lineWidth = 1
  }

  setFillColor(color: string) {
    this.fill = color
  }

  setNewTarget(target: Coords.Coordinate) {
    const slopeToTarget = Coords.Slope.getSlopeFromCoordinates(
      this.location,
      target,
    )
    const vectorSlope = Coords.Vector2d.getVectorSlope(
      slopeToTarget,
      this.vector.magnitude,
    )
    this.vector = new Coords.Vector2d(vectorSlope)
    this.saveLocation()
  }

  reverseDx() {
    this.vector = this.vector.flipX()
    this.saveLocation()
  }

  reverseDy() {
    this.vector = this.vector.flipY()
    this.saveLocation()
  }

  containsPoint(point: Coords.Coordinate): boolean {
    return point.distanceTo(this.location) < this.radius
  }

  setNewVelocity(magnitude: number) {
    this.vector = this.vector.newMagnitude(magnitude)
  }

  setNewVector(v: Coords.Vector2d) {
    this.vector = v
    this.saveLocation()
  }

  private saveLocation() {
    this.locationHistory.add(this.location)
    if (this.locationHistory.size > this.locationHistorySize) {
      this.locationHistory.delete(this.locationHistory.values().next().value)
    }
  }
}
